import { AfterViewInit, ChangeDetectorRef, Component, EventEmitter, Input, OnDestroy, OnInit, Output } from '@angular/core';
import { Subject } from 'rxjs';
import { FormBuilder } from '@angular/forms';
import { MatFormField } from '@angular/material/form-field';
import { FormService } from '../../../../services/base/form.service';
import { AuthenticationService } from '../../../../services/base/authentication.service';
import { take, takeUntil } from 'rxjs/operators';
import { FiltersService } from '../../../../services/base/filters.service';
import { ApiService } from '../../../../services/base/api.service';
import { FilterValue } from '../../../../interfaces/base/filter-value';
import { Filters } from '../../../../interfaces/base/filters';
import { Source } from '../../../../interfaces/routering/source';
import { Tile } from '../../../../interfaces/routering/tile';
import { Action } from '../../../../interfaces/routering/action';
import { Current } from '../../../../interfaces/configuration/current';
import { StandardReaction } from '../../../../interfaces/configuration/standard-reaction';
import { MtxDatetimepickerInputEvent } from '@ng-matero/extensions/datetimepicker';
import * as moment from 'moment/moment';

@Component({
  selector: 'fury-tasks-filters',
  templateUrl: './filters.component.html',
  styleUrls: ['./filters.component.scss'],
})

export class ReportsFiltersComponent implements OnInit, AfterViewInit, OnDestroy {

  public filters: Filters = {
    sources: {
      preload: true,
      isLoading: false,
      data: <Source[]>[]
    },
    tiles: {
      preload: true,
      isLoading: false,
      data: <Tile[]>[]
    },
    actions: {
      preload: true,
      isLoading: false,
      data: <Action[]>[]
    },
    currents: {
      preload: true,
      isLoading: false,
      data: <Current[]>[]
    },
    impactRegistrations: {
      preload: true,
      isLoading: false,
      data: <Source[]>[]
    },
    standardReactions: {
      preload: false,
      isLoading: false,
      subscription: null,
      data: <StandardReaction[]>[]
    },
  };

  @Input() clearFilters: EventEmitter<any> = new EventEmitter<any>();
  @Output() closed: EventEmitter<any> = new EventEmitter();
  @Output() updateFilters: EventEmitter<any> = new EventEmitter<any>();
  @Output() triggerFiltering: EventEmitter<any> = new EventEmitter<any>();

  public form = this.fb.group({
    created_at_start: <string>null,
    created_at_end: <string>null,
    closed_at_start: <string>null,
    closed_at_end: <string>null,
    new_rd_lat: <string>null,
    new_rd_lng: <string>null,
    tile_id: <number>null,
    address: <string>null,
    source_id: <number>null,
    action_id: <number>null,
    current_id: <number>null,
    impact_registration_id: <number>null,
    outside_number: <string>null,
  });

  private destroy$: Subject<null> = new Subject<null>();

  constructor(private fb: FormBuilder,
              private filtersService: FiltersService,
              private cd: ChangeDetectorRef,
              private api: ApiService,
              public formService: FormService,
              public authenticationService: AuthenticationService,
  ) {
  }

  ngOnInit(): void {
    this.formService.form = this.form;

    Object.keys(this.form.controls).map((key: string): void => {
      if (typeof this.api.routeParams.filters[key] !== 'undefined') {
        this.form.controls[key].setValue(this.api.routeParams.filters[key]);
      }
    });

    this.filtersService.filters = this.filters;

    Object.keys(this.filters).map((key: string): void => {
      if (this.filters[key].preload) {
        this.filters[key].isLoading = true;

        this.filtersService[key]().pipe(takeUntil(this.destroy$)).subscribe((data: any): void => {
          this.filters[key].data = data;
          this.filters[key].isLoading = false;
        });
      }
    });

    this.clearFilters.subscribe((): void => {
      this.form.reset();
      this.form.updateValueAndValidity();
    });
  }

  ngAfterViewInit(): void {
    this.api.preSetFilters.pipe(
      take(1),
      takeUntil(this.destroy$)
    ).subscribe((values: FilterValue[]): void => {
      values.forEach((value: FilterValue): void => {
        if (typeof this.form.controls[value.name] !== 'undefined') {
          this.form.controls[value.name].setValue(value.value, {emitEvent: false});
        }
      });
    });

    this.cd.detectChanges();
  }

  getTooltipText(e: MatFormField): string | null {
    const label: HTMLLabelElement = e._elementRef.nativeElement.querySelector('.mat-form-field-label') as HTMLLabelElement;
    const matLabel: HTMLLabelElement = e._elementRef.nativeElement.querySelector('.mat-form-field-label mat-label') as HTMLLabelElement;

    if (matLabel) {
      const children: HTMLCollection = matLabel.children;
      for (let i: number = 0; i < children.length; i++) {
        if (children[i].tagName === 'SPAN') {
          return label.scrollWidth > label.clientWidth ? children[i].innerHTML : null;
        }
      }
    }

    return null;
  }

  setTime(location: 'created_at_start' | 'created_at_end' | 'closed_at_start' | 'closed_at_end', event: MtxDatetimepickerInputEvent<Date>): void {
    this.form.controls[location].setValue(moment(event.value).toISOString());
  }

  clearValue(field: string): void {
    this.form.get(field).setValue(null);
  }

  close(): void {
    this.form.reset();
    this.form.updateValueAndValidity();
    this.updateFilters.emit(this.form.value);
    this.closed.next();
  }

  closeAndFilter(): void {
    this.updateFilters.emit(this.form.value);
    this.closed.next();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }
}
